import { buildAxiosInstance, buildMakeUseAxios, useSuspense } from "@api";
import type { ISetting, ISettingMaster, ISettingsUsageCount } from "@bbj-types/setting";
import type { ObjectId } from "@bbj-types/utils";
import type { AxiosPromise } from "axios";
import { useCallback } from "react";

export const settingsAxios = buildMakeUseAxios(`${import.meta.env.VITE_SETTING_SERVICE_URL}/settings`);
export const settingsAxiosInstance = buildAxiosInstance(`${import.meta.env.VITE_SETTING_SERVICE_URL}/settings`);

export const useGetSettingsByDomainAndType = () => {
  const [state, execute, cancel] = settingsAxios(
    {
      url: "/",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    ({ domain, type }) => {
      return execute({
        url: `/${domain}/${type}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetSettings = () => {
  const [state, execute, cancel] = settingsAxios(
    {
      url: "",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (filters) => {
      return execute({
        url: "",
        params: filters,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export type IPostInitSettings =
  | {
      domain: string[];
      settingType: string[];
    }
  | { _id: string[] };

export const usePostInitSettings = () => {
  const [state, execute, cancel] = settingsAxios<
    ISettingMaster[],
    { settings: IPostInitSettings; organisation?: ObjectId },
    unknown
  >(
    {
      url: "/init",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (settings: IPostInitSettings, organisation?: ObjectId) => {
      return execute({
        data: { settings, organisation },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useSupensePostInitSettings = (settings: IPostInitSettings, organisation?: ObjectId) => {
  return useSuspense(
    () =>
      settingsAxiosInstance.post<ISettingMaster[]>(
        "/init",
        { settings, organisation },
        {
          withCredentials: true,
        }
      ),
    [settings, organisation]
  );
};

export const getSettingsByValues = async (type: string, domain: string, value: string) => {
  const regex = new RegExp(value, "i");
  try {
    const response = await settingsAxiosInstance.get<ISettingMaster>(
      `${import.meta.env.VITE_SETTING_SERVICE_URL}/settings/${domain}/${type}`,
      {
        withCredentials: true,
      }
    );
    if (response.status === 200) {
      return response.data.values.filter((value) => value.active === true && regex.test(value.label));
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const usePatchSetting = () => {
  const [state, execute, cancel] = settingsAxios<void>(
    {
      url: "/{id}",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, setting: ISettingMaster): AxiosPromise<void> => {
      return execute({
        url: `/${id}`,
        data: setting,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface IPayloadSettingValue {
  value?: string;
  label: string;
  active?: boolean;
  createByDefault?: boolean;
  searchByDefault?: boolean;
  isAutoTask?: boolean;
  metadata?: object;
}

export interface IPostSetting {
  label: string;
  settingType?: string;
  domain: string;
  values?: IPayloadSettingValue[];
  public?: boolean;
}

export const usePostSetting = () => {
  const [state, execute, cancel] = settingsAxios<ISetting, IPostSetting, unknown>(
    {
      url: "",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (setting: IPostSetting): AxiosPromise<ISetting> => {
      return execute({
        data: setting,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

const settingsAxiosV2 = buildMakeUseAxios(`${import.meta.env.VITE_SETTING_SERVICE_URL}/v2/settings`);

export const useGetUsageCount = () => {
  const [state, execute, cancel] = settingsAxiosV2<ISettingsUsageCount>(
    {
      url: "/usageCount",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  return [state, execute, cancel] as const;
};

export * from "./getPublicInitSettings";
export * from "./getTeams";
export * from "./getUserAllOrganisationsSettings";
export * from "./getFupAdditionalFields";
export * from "./getFupLogos";
